import { SimilarFilesResponse } from "@/api";
import { LocalFile } from "@/hooks/types";
import Image from "@ingka/image";
import Text from "@ingka/text";
import { SimilaritySearchAssetDto } from "generated/api";
import { useMemo } from "react";
import { ShowSimilarImages } from "../SimilarImages/ShowSimilarImages";
import SimilarImagesList from "../SimilarImages/SimilarImageList";
import { FileToUploadItem } from "./FileToUploadItem";
import { validateImageFile } from "./utils";
import Skeleton from "@ingka/skeleton";

type ImageFilesToUploadProps = {
  isLoadingSimilarAssets: boolean;
  localFiles: LocalFile[];
  similarFiles: SimilarFilesResponse;
  uploadProgress: Record<string, number>;
  removeFile: (file: LocalFile) => void;
  retryUpload: (fileUrl: string) => void;
  setSelectedFile: (filename: string) => void;
};

export const ImageFilesToUpload = ({
  isLoadingSimilarAssets,
  localFiles,
  uploadProgress,
  removeFile,
  retryUpload,
  similarFiles,
  setSelectedFile,
}: ImageFilesToUploadProps) => {
  const topSimilarHits = useMemo(() => {
    return Object.values(similarFiles)
      .map((hit) => hit.result)
      .reduce((acc, similarityResults) => {
        const topHit = similarityResults.find((result) => result.score > 0.5);
        if (topHit) {
          acc.push(topHit);
        }
        return acc;
      }, [] as SimilaritySearchAssetDto[]);
  }, [similarFiles]);

  return (
    <>
      <ul className="grid grid-cols-5 gap-x-space125 gap-y-space100">
        {localFiles.map((localFile, index) => {
          const validationResult = validateImageFile(
            localFile.file,
            localFile.dimensions,
          );
          const filename = localFile.file.name;
          const progress = uploadProgress[localFile.url];
          const warningMessage = !validationResult.isValid
            ? validationResult.message
            : undefined;

          return (
            <FileToUploadItem
              key={`${filename}-${index}`}
              localFile={localFile}
              progress={progress}
              onRemove={() => removeFile(localFile)}
              onRetry={() => retryUpload(localFile.url)}
              isRetrying={localFile.uploadStatus === "uploading"}
              mediaElement={
                <Image
                  className="size-full object-cover"
                  src={localFile.url}
                  alt={filename}
                />
              }
              warningMessage={warningMessage}
              similarContent={
                isLoadingSimilarAssets ? (
                  <Skeleton className="h-[1.75rem]" />
                ) : (
                  similarFiles[filename] && (
                    <ShowSimilarImages
                      filename={filename}
                      onShowSimilarImagesClicked={setSelectedFile}
                    />
                  )
                )
              }
            />
          );
        })}
      </ul>

      {topSimilarHits.length > 0 && (
        <>
          <Text tagName="h3" headingSize="xs" className="mt-space250">
            The imported asset(s) may already exist in Ingka DAM
          </Text>
          <Text tagName="p" bodySize="s" className="mb-space100">
            Please review the similar asset(s) below to avoid unnecessary
            uploads
          </Text>
          <SimilarImagesList similarFiles={topSimilarHits} />
        </>
      )}
    </>
  );
};
